import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, InputField, Tag } from '../UI';
import { AddProfessionMenu } from './AddProfessionMenu';
import type {
  MandatoryStaffCompetenceRequirement,
  StaffProfessionResponse
} from '../../api/generated';
import {
  DEFAULT_POST_PROCESSING_MINUTES,
  DEFAULT_PRE_PROCESSING_MINUTES,
  DEFAULT_REQUIREMENT_COUNT,
  isDistinctnessApplicable,
  professionIds,
  type MandatoryRow
} from '../../services/interventionTemplateMappers';
import styles from './InterventionFormModal.module.css';

const PROFESSION_SEPARATOR = '/';

interface MandatoryStaffRequirementRowProps {
  row: MandatoryRow;
  // Active competences the row may still add; archived ones are only ever shown, never offered.
  competences: StaffProfessionResponse[];
  professionNameById: Map<string, string>;
  error?: string;
  onChange: (rowId: string, patch: Partial<MandatoryStaffCompetenceRequirement>) => void;
  onRemove: (rowId: string) => void;
}

// One "Required" row: N staff, each holding any one of the row's competences, with the
// option that the N staff must bring N different ones. A row with no competences books
// headcount only and is shown with a fallback label rather than dropped from the table.
export const MandatoryStaffRequirementRow = ({
  row,
  competences,
  professionNameById,
  error,
  onChange,
  onRemove
}: MandatoryStaffRequirementRowProps) => {
  const { t } = useTranslation();
  const ids = professionIds(row);
  const remaining = competences.filter((c) => !ids.includes(c.id));
  const hasDistinctOption = isDistinctnessApplicable(row);

  const addProfession = (competence: StaffProfessionResponse): void =>
    onChange(row.rowId, { staffProfessionIds: [...ids, competence.id] });

  const removeProfession = (id: string): void =>
    onChange(row.rowId, { staffProfessionIds: ids.filter((other) => other !== id) });

  return (
    <div className={styles.skillRowGroup}>
      <div className={styles.skillRow}>
        <div className={`${styles.skillColName} ${styles.professionChips}`}>
          {ids.length === 0 && <span className={styles.skillName}>{t('config-iv-no-skill')}</span>}
          {ids.map((id, index) => {
            const name = professionNameById.get(id) ?? id;
            return (
              <Fragment key={id}>
                {index > 0 && <span className={styles.professionSeparator}>{PROFESSION_SEPARATOR}</span>}
                <Tag
                  label={name}
                  onRemove={() => removeProfession(id)}
                  removeAriaLabel={t('config-iv-remove-profession', { name })}
                />
              </Fragment>
            );
          })}
          <AddProfessionMenu
            competences={remaining}
            ariaLabel={t('config-iv-add-alternative')}
            onSelect={addProfession}
          />
        </div>
        <InputField
          className={styles.skillColCount}
          type="number"
          value={row.count ?? DEFAULT_REQUIREMENT_COUNT}
          min={1}
          onChange={(e) => onChange(row.rowId, { count: Number(e.target.value) })}
        />
        <InputField className={styles.skillColTime}>
          <input
            type="number"
            className={styles.innerNumberInput}
            value={row.preProcessingMinutes ?? DEFAULT_PRE_PROCESSING_MINUTES}
            min={0}
            onChange={(e) => onChange(row.rowId, { preProcessingMinutes: Number(e.target.value) })}
          />
          <span className={styles.timeUnit}>{t('room-booking-minutes-suffix')}</span>
        </InputField>
        <InputField className={styles.skillColTime}>
          <input
            type="number"
            className={styles.innerNumberInput}
            value={row.postProcessingMinutes ?? DEFAULT_POST_PROCESSING_MINUTES}
            min={0}
            onChange={(e) => onChange(row.rowId, { postProcessingMinutes: Number(e.target.value) })}
          />
          <span className={styles.timeUnit}>{t('room-booking-minutes-suffix')}</span>
        </InputField>
        <button className={styles.removeBtn} onClick={() => onRemove(row.rowId)} type="button">
          ×
        </button>
      </div>
      {/* The error renders independently of the checkbox: a row loaded from the server can be
          unsatisfiable while holding too few professions to offer the checkbox at all, and an
          error with nowhere to show would block saving with nothing marked wrong. */}
      {(hasDistinctOption || error) && (
        <div className={styles.distinctRow}>
          {hasDistinctOption && (
            <Checkbox
              id={`distinct-${row.rowId}`}
              isChecked={row.distinctCompetences ?? false}
              onChange={(isChecked) => onChange(row.rowId, { distinctCompetences: isChecked })}
              label={t('config-iv-distinct')}
            />
          )}
          {error && (
            <span className={styles.rowError} role="alert">
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
